import { store } from 'redux/store';
import { useParams } from 'react-router-dom';
import { State } from 'redux/initialState';
import { useSelector } from 'react-redux';
import { FC, ReactNode, createContext, useContext, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import asyncActionSwal from 'utils/asyncActionSwal';
import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';

import actionTypes from 'constants/actionTypes';
import sizes from 'constants/sizes';

import { setModal } from 'modules/_shared/redux/modalActions';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { useGetShare } from 'modules/partners/hooks/useGetShare';
import { useGetPartnersSectionPermissions } from 'modules/partners/hooks/useGetPartnerSectionsPermissions';
import { AddEncumbranceModalProvider } from 'modules/partners/modals/AddEncumbranceModal/context/AddEncumbranceModalContext';
import AddEncumbranceModal from 'modules/partners/modals/AddEncumbranceModal';
import { AddCoOwnerModalProvider } from 'modules/partners/modals/AddCoOwnerModal/context/AddCoOwnerModalContext';
import { useGetEncumbrance } from 'modules/partners/hooks/useGetEncumbrance';
import AddCoOwnerModal from 'modules/partners/modals/AddCoOwnerModal';
import { useGetCoOwners } from 'modules/partners/hooks/useGetCoOwners';
import { deleteCoOwner } from 'modules/partners/redux/coOwnerActions';

import { EncumbranceDetailsPageContextType } from '../types/EncumbranceDetailsPageContextType';
import { encumbranceDetailPageInitValue } from '../constants/encumbranceDetailsPageInitValue';

const EncumbranceDetailsPageContext =
  createContext<EncumbranceDetailsPageContextType>(
    encumbranceDetailPageInitValue
  );

export const EncumbranceDetailsPageProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { t } = useTranslate();
  const { shareId, encumbranceId } = useParams();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const coOwners = useSelector((state: State) => state.coOwners);

  useGetActualSociety(actualSociety?._id);
  useGetMenu();

  const { data: permissionsData } = useGetPartnersSectionPermissions();

  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [refetchKey, setRefetchKey] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: shareData } = useGetShare({
    shareId,
  });

  const { data: encumbranceData } = useGetEncumbrance({
    encumbranceId,
    refetchKey,
  });

  const { isLoading: isLoadingCoOwners } = useGetCoOwners({
    encumbranceId,
  });

  const invalidateEncumbrance = () => {
    setRefetchKey((v) => v + 1);
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleEditEncumbrance = () => {
    if (!encumbranceData?.encumbrance || !shareData.share) return;

    store.dispatch(
      setModal(
        <AddEncumbranceModalProvider
          share={shareData?.share}
          encumbrance={encumbranceData?.encumbrance}
          modalActionType={actionTypes.EDIT}
          size={sizes.XL}
          onEditEncumbranceSuccess={invalidateEncumbrance}
        >
          <AddEncumbranceModal />
        </AddEncumbranceModalProvider>
      )
    );
  };

  const handleRemoveEncumbrance = () => {};

  const handleAddCoOwner = () => {
    if (!encumbranceData?.encumbrance) return;

    store.dispatch(
      setModal(
        <AddCoOwnerModalProvider
          encumbrance={encumbranceData?.encumbrance}
          modalActionType={actionTypes.ADD}
          onAddCoOwnerSuccess={invalidateEncumbrance}
        >
          <AddCoOwnerModal />
        </AddCoOwnerModalProvider>
      )
    );
  };

  const handleEditCoOwner = (index: number) => {
    const coOwner = coOwners?.[index];

    if (!encumbranceData?.encumbrance || !coOwner) return;

    store.dispatch(
      setModal(
        <AddCoOwnerModalProvider
          encumbrance={encumbranceData?.encumbrance}
          coOwner={coOwner}
          modalActionType={actionTypes.EDIT}
          onEditCoOwnerSuccess={invalidateEncumbrance}
        >
          <AddCoOwnerModal />
        </AddCoOwnerModalProvider>
      )
    );
  };

  const handleRemoveCoOwner = (index: number) => {
    const coOwner = coOwners[index];

    asyncActionSwal({
      swalTitle: t('AreYouSureAboutDelEncumbrance'),
      swalElementName: coOwner?.name || '',
      swalInfoText: t('ThisProcedureCantUndo'),
      swalConfirmButtonText: t('OkDelete'),
      callback: async () => {
        try {
          if (!coOwner?._id) return;
          setIsLoading(true);

          await store.dispatch(
            deleteCoOwner({
              coOwnerId: coOwner._id,
            })
          );

          invalidateEncumbrance();
          successSwal(t('CoOwnerDeletedSuccessfully'));
        } catch (error) {
          errorSwal(t('ErrorDeletingCoOwner'));
        } finally {
          setIsLoading(false);
        }
      },
    });
  };

  return (
    <EncumbranceDetailsPageContext.Provider
      value={{
        data: {
          type: encumbranceData.encumbrance?.type || '',
          from: encumbranceData.encumbrance?.from || 0,
          to: encumbranceData.encumbrance?.to || 0,
          date: encumbranceData.encumbrance?.date || '',
          endDate: encumbranceData.encumbrance?.endDate || '',
          scriptureData: encumbranceData.encumbrance?.scriptureData || {
            notaryName: '',
            protocolNr: '',
            notaryCity: '',
          },
          document: null,
          coOwners: coOwners || [],
          coOwnersTotal: encumbranceData.encumbrance?.coOwnersCount?.total || 0,
          page,
          rowsPerPage,
          coOwnersPercentage:
            encumbranceData.encumbrance?.coOwnersPercentage || 0,
          isAdmin: permissionsData?.isAdmin || false,
          isReadOnly: permissionsData?.isReadOnly || false,
          isLoading: isLoadingCoOwners || isLoading,
        },
        actions: {
          handleChangePage,
          handleChangeRowsPerPage,
          handleEditEncumbrance,
          handleRemoveEncumbrance,
          handleAddCoOwner,
          handleEditCoOwner,
          handleRemoveCoOwner,
        },
      }}
    >
      {children}
    </EncumbranceDetailsPageContext.Provider>
  );
};

export const useEncumbranceDetailsPage = () => {
  const context = useContext(EncumbranceDetailsPageContext);
  if (!context) {
    throw new Error(
      'useEncumbranceDetailsPage must be used within a EncumbranceDetailsPageProvider'
    );
  }
  return context;
};
